// @owners { team: patients }
import { PAYMENT_ERRORS } from '~shared/features/payment-methods/constants';
import { post } from '~shared/helpers/apiHelper';
import { useMutation } from '~shared/react-query';
import { Sentry } from '~shared/sentry';
import { type APIError } from '~shared/types';

export type AchBalanceCheckResponse = {
  sufficient: boolean;
};

// buildLegacyError spreads the BE body's `details` into error.details, so code lands at details.details.code.
const normalizeAchBalanceCheckError = (error: APIError): APIError => {
  const nestedDetails = (error.details as { details?: { code?: string; message?: string } }).details;
  if (!error.details.code && nestedDetails?.code) {
    error.details.code = nestedDetails.code;
    if (nestedDetails.message) {
      error.details.message = nestedDetails.message;
    }
  }
  return error;
};

const isKnownAchBalanceCheckError = (error: APIError) => {
  const code = error.details?.code;
  return code === PAYMENT_ERRORS.ACH_INSUFFICIENT_FUNDS || code === PAYMENT_ERRORS.PLAID_ITEM_STALE;
};

export const useAchBalanceCheck = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      paymentMethodId,
      estimatedCost,
    }: {
      paymentMethodId: number;
      estimatedCost: number;
    }): Promise<AchBalanceCheckResponse> => {
      const response = await post(
        '/payment_methods/ach_balance_check',
        { payment_method_id: paymentMethodId, estimated_cost: estimatedCost },
        { version: 'v3' },
      );
      if (response.error) {
        throw normalizeAchBalanceCheckError(response.error);
      }
      return response as AchBalanceCheckResponse;
    },
    onError: (error: APIError) => {
      if (!isKnownAchBalanceCheckError(error)) {
        Sentry.captureException(error);
      }
    },
  });

  const checkAchBalance = (paymentMethodId: number, estimatedCost: number) =>
    mutateAsync({ paymentMethodId, estimatedCost });

  return { checkAchBalance, isPending };
};
